/**
 * The train loop's shape: the ten stages in loop order, where each sits
 * in the diagram, and the edges between them. The stages are the same for
 * every team. What differs per team is each stage's state, which the API
 * reports (TrainLoopStatus) and the diagram and the list paint on top.
 *
 * A gate is a stage whose output waits on a person: a flag has to be
 * marked, a PR merged, a model made the default. The diagram draws gates
 * with pointed ends. Benchmark PRs takes two inputs, reviewed flags and
 * the monitor's own findings, and inference feeds the monitor again.
 *
 * The diagram is two rows of five. The loop reads left to right along the
 * top row, drops down at the right edge, reads right to left along the
 * bottom row, and climbs back to the start at the left edge.
 *
 * Copy gate: `label`, `summary` and the captions render on the page, so
 * the CLAUDE.md rules apply (no em dashes, no trailing tails).
 */

import type {
  TrainLoopLink as PipelineLink,
  TrainLoopLinkFired,
  TrainLoopLinkProgress,
  TrainLoopLinkRun,
  TrainLoopStageStatus,
  TrainLoopStatus,
} from '@/api/generated/model'
import { formatRelativeTime } from '@/lib/utils'

export type TrainLoopStageKey = keyof TrainLoopStatus

export interface TrainLoopLink {
  label: string
  to: string
  /** The sidebar page the link depends on; the link hides with it (TRA-927). */
  navRoute?: string
}

export interface TrainLoopNode {
  key: TrainLoopStageKey
  label: string
  /** What the stage does, the same for every team. */
  summary: string
  gate: boolean
  /** Where the stage is configured or watched, when the product has a page for it. */
  link?: TrainLoopLink
  column: number
  row: number
}

/** The stages, in loop order. The list numbers them from this order. */
export const TRAIN_LOOP_NODES: readonly TrainLoopNode[] = [
  {
    key: 'behavior_monitor',
    label: 'Behavior monitor',
    summary: 'A judge reads a sample of production trajectories each night and flags issues.',
    gate: false,
    link: { label: 'Models', to: '/models', navRoute: '/models' },
    column: 0,
    row: 0,
  },
  {
    key: 'review',
    label: 'Review',
    summary: 'A person marks each flag good or bad in the review queue.',
    gate: true,
    link: { label: 'Review scans', to: '/settings/review-scans', navRoute: '/review' },
    column: 1,
    row: 0,
  },
  {
    key: 'benchmark_prs',
    label: 'Benchmark PRs',
    summary:
      'Reviewed flags and monitor findings become tests. Each batch is opened as a PR in your repository.',
    gate: false,
    link: { label: 'GitHub App', to: '/settings/github-app', navRoute: '/review' },
    column: 2,
    row: 0,
  },
  {
    key: 'benchmark_merge',
    label: 'Benchmark merge',
    summary: 'A person merges the PR. Merged tests join the eval group.',
    gate: true,
    column: 3,
    row: 0,
  },
  {
    key: 'failure_analysis',
    label: 'Failure analysis',
    summary: 'Trajectories that fail a test are traced to where they went wrong.',
    gate: false,
    column: 4,
    row: 0,
  },
  {
    key: 'task_generation',
    label: 'Task generation',
    summary: 'Failures and benchmark tests become training tasks.',
    gate: false,
    column: 4,
    row: 1,
  },
  {
    key: 'training',
    label: 'Training',
    summary: 'The model trains on the generated tasks.',
    gate: false,
    link: { label: 'Rollout secrets', to: '/settings/rollout-secrets' },
    column: 3,
    row: 1,
  },
  {
    key: 'evaluation',
    label: 'Evaluation',
    summary: 'The trained model is scored on the eval group.',
    gate: false,
    link: { label: 'Evals', to: '/evals', navRoute: '/evals' },
    column: 2,
    row: 1,
  },
  {
    key: 'model_promotion',
    label: 'Model promotion',
    summary: 'A person makes the scored model the team default.',
    gate: true,
    link: { label: 'Models', to: '/models', navRoute: '/models' },
    column: 1,
    row: 1,
  },
  {
    key: 'inference',
    label: 'Inference',
    summary:
      'The default model serves production traffic through the gateway, and its traces start the next night.',
    gate: false,
    link: { label: 'API keys', to: '/settings/api-keys' },
    column: 0,
    row: 1,
  },
]

export interface TrainLoopEdge {
  from: TrainLoopStageKey
  to: TrainLoopStageKey
  /** A caption on the two edges that need one: the second input and the loop back. */
  caption?: string
}

export const TRAIN_LOOP_EDGES: readonly TrainLoopEdge[] = [
  { from: 'behavior_monitor', to: 'review' },
  { from: 'review', to: 'benchmark_prs' },
  { from: 'behavior_monitor', to: 'benchmark_prs', caption: 'Monitor findings' },
  { from: 'benchmark_prs', to: 'benchmark_merge' },
  { from: 'benchmark_merge', to: 'failure_analysis' },
  { from: 'failure_analysis', to: 'task_generation' },
  { from: 'task_generation', to: 'training' },
  { from: 'training', to: 'evaluation' },
  { from: 'evaluation', to: 'model_promotion' },
  { from: 'model_promotion', to: 'inference' },
  { from: 'inference', to: 'behavior_monitor', caption: 'Traces' },
]

export const STATUS_LABELS: Record<TrainLoopStageStatus, string> = {
  on: 'On',
  off: 'Off',
}

// ── Events (TRA-1354) ────────────────────────────────────────────────
// The links' `after` lists hold event names. The three outside events
// have fixed words; `<slug>.finished` names a job, whose display name
// comes from the links list, or from this map when the job is not a link
// (nightly-evals has no link of its own).

const OUTSIDE_EVENT_LABELS: Record<string, string> = {
  'turn.completed': 'a completed trajectory',
  'label.reviewed': 'a saved review',
  'pull-request.merged': 'a merged pull request',
}

const JOB_NAMES_WITHOUT_LINK: Record<string, string> = {
  'nightly-evals': 'Nightly evals',
}

const FINISHED_SUFFIX = '.finished'

/** One event name in words, for the "Starts after" line. */
export function describeEvent(event: string, jobNames: ReadonlyMap<string, string>): string {
  const outside = OUTSIDE_EVENT_LABELS[event]
  if (outside !== undefined) return outside
  if (event.endsWith(FINISHED_SUFFIX)) {
    const slug = event.slice(0, -FINISHED_SUFFIX.length)
    const name = jobNames.get(slug) ?? JOB_NAMES_WITHOUT_LINK[slug] ?? slug
    return `${name} finishes`
  }
  return event
}

/** The `after` list in words: "a saved review or Dream scan finishes". */
export function describeStartsAfter(
  after: readonly string[],
  jobNames: ReadonlyMap<string, string>,
): string {
  return after.map((event) => describeEvent(event, jobNames)).join(' or ')
}

// A link's state chip, the answer under its row and its last run, in words.

export type LinkState = 'running' | 'off' | 'ready'

const RUN_OUTCOME_LABELS: Record<TrainLoopLinkRun['outcome'], string> = {
  active: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
}

export function linkState(link: PipelineLink): LinkState {
  if (link.active_job_name !== null) return 'running'
  if (link.unavailable_reason !== null) return 'off'
  return 'ready'
}

/** The answer under the row, in words. */
export function firedText(fired: TrainLoopLinkFired): string {
  if (fired.outcome === 'started') return `Started ${fired.detail}`
  return fired.detail
}

/** The progress cell: "3 / 10 unit", or that the link is off at threshold 0. */
export function progressText(progress: TrainLoopLinkProgress): string {
  if (progress.threshold === 0) return 'This link is off. Set a threshold above 0 to let events start it.'
  return `${String(progress.count)} / ${String(progress.threshold)} ${progress.unit}`
}

/** The last-run cell: "Succeeded 2h ago". */
export function lastRunText(run: TrainLoopLinkRun): string {
  const when = run.started_at === null ? '' : ` ${formatRelativeTime(run.started_at)}`
  return `${RUN_OUTCOME_LABELS[run.outcome]}${when}`
}

/** Zero turns the link off for events; a start by hand still runs. */
export const THRESHOLD_MIN = 0
export const THRESHOLD_MAX = 100000

/** Why the typed threshold cannot be saved, or null. */
export function thresholdError(text: string): string | null {
  const value = Number(text)
  if (
    text.trim() === '' ||
    !Number.isInteger(value) ||
    value < THRESHOLD_MIN ||
    value > THRESHOLD_MAX
  ) {
    return `The threshold must be a whole number between ${String(THRESHOLD_MIN)} and ${String(THRESHOLD_MAX)}.`
  }
  return null
}
